import type { ChronologyEvent, ChronologyEventOverrides } from "~/api/model";

/**
 * Compares two nullable page numbers. Events with a page number come before
 * events without one; among two paged events, lower page wins.
 * Returns 0 when both are null or equal so the caller can fall through.
 */
const comparePageNumber = (a: number | null, b: number | null): number => {
  if (a != null && b != null) return a - b;
  if (a != null && b == null) return -1;
  if (a == null && b != null) return 1;
  return 0;
};

/**
 * Sorts chronology events by (1) eventDate ascending, then (2) source PDF
 * page number ascending. Events without a date are pushed to the end (the
 * "Undated" bucket). Within each tier, createdAt is the final stable
 * tie-breaker so the list never jumps around between renders.
 */
export const sortChronologyEvents = (
  events: ChronologyEvent[]
): ChronologyEvent[] => {
  return [...events].sort((a, b) => {
    if (a.eventDate && b.eventDate) {
      const dateDiff = a.eventDate.localeCompare(b.eventDate);
      if (dateDiff !== 0) return dateDiff;
      const pageDiff = comparePageNumber(
        a.reference.pageNumber,
        b.reference.pageNumber
      );
      if (pageDiff !== 0) return pageDiff;
      return a.createdAt.localeCompare(b.createdAt);
    }
    if (a.eventDate && !b.eventDate) return -1;
    if (!a.eventDate && b.eventDate) return 1;
    const pageDiff = comparePageNumber(
      a.reference.pageNumber,
      b.reference.pageNumber
    );
    if (pageDiff !== 0) return pageDiff;
    return a.createdAt.localeCompare(b.createdAt);
  });
};

/**
 * Returns the set of field names that currently carry a manual override for the
 * given event. Used to surface "edited" affordances per field. A field that has
 * been explicitly blanked (set to null) still counts as an override.
 */
export const getOverriddenFields = (
  event: ChronologyEvent
): Set<keyof ChronologyEventOverrides> => {
  const { overrides } = event;
  if (!overrides) return new Set();
  const keys = Object.keys(overrides) as (keyof ChronologyEventOverrides)[];
  return new Set(keys.filter((key) => overrides[key] !== undefined));
};
